import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { db } from '@/lib/firebase/config';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy, Timestamp } from 'firebase/firestore';
import { Loader2, Plus, Pencil, Trash2, GripVertical } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

interface AssemblyMember {
  id: string;
  name: { ar: string; en: string };
  title: { ar: string; en: string };
  credentials: { ar: string; en: string };
  image: string;
  order: number;
}

export function GeneralAssemblyMembersSection() {
  const [members, setMembers] = useState<AssemblyMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<AssemblyMember | null>(null);
  const [formData, setFormData] = useState({
    name: { ar: '', en: '' },
    title: { ar: '', en: '' },
    credentials: { ar: '', en: '' },
    image: '',
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const membersQuery = query(
        collection(db, 'landing_assembly_members'),
        orderBy('order', 'asc')
      );
      const snapshot = await getDocs(membersQuery);
      const membersData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AssemblyMember[];
      
      setMembers(membersData);
    } catch (error) {
      console.error('Error fetching assembly members:', error);
      toast({
        title: 'خطأ',
        description: 'فشل في تحميل أعضاء الجمعية العمومية',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingMember(null);
    setFormData({
      name: { ar: '', en: '' },
      title: { ar: '', en: '' },
      credentials: { ar: '', en: '' },
      image: '',
    });
    setIsEditModalOpen(true);
  };

  const handleEdit = (member: AssemblyMember) => {
    setEditingMember(member);
    setFormData({
      name: typeof member.name === 'string' ? { ar: member.name, en: '' } : member.name,
      title: typeof member.title === 'string' ? { ar: member.title, en: '' } : member.title,
      credentials: typeof member.credentials === 'string' ? { ar: member.credentials, en: '' } : member.credentials,
      image: member.image,
    });
    setIsEditModalOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.ar || !formData.title.ar) {
      toast({
        title: 'خطأ',
        description: 'يجب إدخال الاسم والمنصب على الأقل',
        variant: 'destructive',
      });
      return;
    }

    try {
      if (editingMember) {
        // Update existing member
        await updateDoc(doc(db, 'landing_assembly_members', editingMember.id), {
          name: formData.name,
          title: formData.title,
          credentials: formData.credentials,
          image: formData.image,
          updatedAt: Timestamp.now(),
        });
        toast({
          title: 'تم التحديث',
          description: 'تم تحديث بيانات العضو بنجاح',
        });
      } else {
        // Add new member
        await addDoc(collection(db, 'landing_assembly_members'), {
          name: formData.name,
          title: formData.title,
          credentials: formData.credentials,
          image: formData.image,
          order: members.length + 1,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
        toast({
          title: 'تمت الإضافة',
          description: 'تمت إضافة العضو بنجاح',
        });
      }

      setIsEditModalOpen(false);
      fetchMembers();
    } catch (error) {
      console.error('Error saving member:', error);
      toast({
        title: 'خطأ',
        description: 'فشل في حفظ البيانات',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('هل أنت متأكد من حذف هذا العضو؟')) return;

    try {
      await deleteDoc(doc(db, 'landing_assembly_members', id));
      toast({
        title: 'تم الحذف',
        description: 'تم حذف العضو بنجاح',
      });
      fetchMembers();
    } catch (error) {
      console.error('Error deleting member:', error);
      toast({
        title: 'خطأ',
        description: 'فشل في حذف العضو',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#7dda55]" />
        </div>
      </Card>
    );
  }

  return (
    <>
      <Card className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-bold">أعضاء الجمعية العمومية</h3>
          <Button onClick={handleAdd} className="bg-[#7dda55] hover:bg-[#6bc444]">
            <Plus className="h-4 w-4 ml-2" />
            إضافة عضو
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {members.map((member) => (
            <Card key={member.id} className="overflow-hidden border-t-4 border-[#7dda55]">
              <div className="p-4">
                <div className="aspect-square mb-3 overflow-hidden rounded-lg">
                  <img
                    src={member.image || 'https://via.placeholder.com/300'}
                    alt={typeof member.name === 'string' ? member.name : member.name.ar}
                    className="w-full h-full object-cover"
                  />
                </div>
                <h4 className="font-bold text-[#004250] mb-1 text-center">{typeof member.name === 'string' ? member.name : member.name.ar}</h4>
                <p className="text-[#7dda55] font-medium text-sm mb-2 text-center">{typeof member.title === 'string' ? member.title : member.title.ar}</p>
                <p className="text-gray-600 text-xs text-center line-clamp-2">{typeof member.credentials === 'string' ? member.credentials : member.credentials.ar}</p>
                
                <div className="flex gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(member)}
                    className="flex-1"
                  >
                    <Pencil className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDelete(member.id)}
                    className="flex-1"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {members.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <p>لا يوجد أعضاء</p>
            <p className="text-sm mt-2">انقر على "إضافة عضو" لإضافة أعضاء الجمعية العمومية</p>
          </div>
        )}
      </Card>

      {/* Edit/Add Dialog */}
      <Dialog open={isEditModalOpen} onOpenChange={setIsEditModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-right">
              {editingMember ? 'تعديل عضو' : 'إضافة عضو جديد'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-3">
              <Label className="text-base font-semibold">الاسم / Name</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name-ar" className="text-sm text-muted-foreground">عربي</Label>
                  <Input
                    id="name-ar"
                    value={formData.name.ar}
                    onChange={(e) => setFormData({ ...formData, name: { ...formData.name, ar: e.target.value } })}
                    placeholder="د. أحمد محمد"
                    dir="rtl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="name-en" className="text-sm text-muted-foreground">English</Label>
                  <Input
                    id="name-en"
                    value={formData.name.en}
                    onChange={(e) => setFormData({ ...formData, name: { ...formData.name, en: e.target.value } })}
                    placeholder="Dr. Ahmed Mohammed"
                    dir="ltr"
                  />
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <Label className="text-base font-semibold">المنصب / Position</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="title-ar" className="text-sm text-muted-foreground">عربي</Label>
                  <Input
                    id="title-ar"
                    value={formData.title.ar}
                    onChange={(e) => setFormData({ ...formData, title: { ...formData.title, ar: e.target.value } })}
                    placeholder="عضو الجمعية العمومية"
                    dir="rtl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="title-en" className="text-sm text-muted-foreground">English</Label>
                  <Input
                    id="title-en"
                    value={formData.title.en}
                    onChange={(e) => setFormData({ ...formData, title: { ...formData.title, en: e.target.value } })}
                    placeholder="General Assembly Member"
                    dir="ltr"
                  />
                </div>
              </div>
            </div>
            <div className="space-y-3">
              <Label className="text-base font-semibold">المؤهلات / Credentials</Label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="credentials-ar" className="text-sm text-muted-foreground">عربي</Label>
                  <Textarea
                    id="credentials-ar"
                    value={formData.credentials.ar}
                    onChange={(e) => setFormData({ ...formData, credentials: { ...formData.credentials, ar: e.target.value } })}
                    placeholder="المؤهلات والخبرات..."
                    className="min-h-[100px]"
                    dir="rtl"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="credentials-en" className="text-sm text-muted-foreground">English</Label>
                  <Textarea
                    id="credentials-en"
                    value={formData.credentials.en}
                    onChange={(e) => setFormData({ ...formData, credentials: { ...formData.credentials, en: e.target.value } })}
                    placeholder="Qualifications and experience..."
                    className="min-h-[100px]"
                    dir="ltr"
                  />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="image">رابط الصورة</Label>
              <Input
                id="image"
                value={formData.image}
                onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                placeholder="https://example.com/image.jpg"
                dir="ltr"
              />
              {formData.image && (
                <img
                  src={formData.image}
                  alt="Preview"
                  className="w-32 h-32 rounded-lg object-cover mt-2"
                />
              )}
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setIsEditModalOpen(false)}>
                إلغاء
              </Button>
              <Button onClick={handleSave} className="bg-[#7dda55] hover:bg-[#6bc444]">
                حفظ
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

